import { useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { EvaluationInsightResult } from '@/modules/simulations/types';
import { INSIGHT_TYPES } from '@/shared/types/insights';
import { formatDate } from '@/util/dateUtils';

interface InsightRowProps {
  insight: EvaluationInsightResult;
}

export const InsightRow = ({ insight }: InsightRowProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const { type, value, error, reasoning } = insight;
  const isError = !!error;

  const resolveValueText = (): string => {
    if (isError) {
      return error;
    }
    if (value === null) {
      return '—';
    }
    if (type === INSIGHT_TYPES.BOOLEAN && typeof value === 'boolean') {
      return value ? t('common.true') : t('common.false');
    }
    if (type === INSIGHT_TYPES.DATETIME && typeof value === 'string') {
      return formatDate(value) || value;
    }
    return String(value);
  };
  const valueText = resolveValueText();

  const showCopy = !isError && value !== null;

  const expansionBody = isError ? error : reasoning;
  const expansionHeader = isError ? t('simulations.error') : t('insights.reasoning');
  const canExpand = !!expansionBody;

  const ChevronIcon = expanded ? ChevronUpIcon : ChevronDownIcon;

  return (
    <div className="flex flex-col gap-3 border-b border-border px-4 py-3 last:border-b-0">
      <div className="flex items-center gap-4 pr-1">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p className="w-full text-xs leading-4 font-medium text-muted-foreground">
            {insight.name}
          </p>
          <div className="flex min-w-0 items-center gap-1.5">
            <p
              className={cn(
                'min-w-0 truncate text-sm leading-6 font-medium',
                isError ? 'text-destructive' : 'text-foreground'
              )}
            >
              {valueText}
            </p>
            {showCopy && <CopyButton data={valueText} iconSize={16} className="shrink-0" />}
          </div>
        </div>
        {canExpand && (
          <Button
            type="button"
            variant="ghost-neutral"
            size="icon-sm"
            onClick={() => setExpanded((prev) => !prev)}
            aria-label={expanded ? t('common.collapse') : t('common.expand')}
          >
            <ChevronIcon />
          </Button>
        )}
      </div>

      {expanded && expansionBody && (
        <div className="flex flex-col gap-0.5">
          <p className="text-xs leading-4 font-medium text-muted-foreground">{expansionHeader}</p>
          <div className="border-l border-border py-1 pl-3">
            <p className="text-xs leading-5 text-foreground">{expansionBody}</p>
          </div>
        </div>
      )}
    </div>
  );
};
